import { FC, useEffect, useRef, useState } from 'react';
import { Input } from '../Input';
import { graphQlClient } from '@config/graphqlClient';
import MapsGraphQL from '@graphql/google-maps-service.queries';
import { getPlacesRequest, Place } from '@models/place.model';

interface GoogleAddressInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'value'
  > {
  value: string;
  onChange: (value: string) => void;
  onChangeCoor?: (lat?: number | null, long?: number | null) => void;
  error?: boolean;
  className?: string;
  dropdownClassName?: string;
}

export const GoogleAddressInput: FC<GoogleAddressInputProps> = ({
  value,
  onChange,
  onChangeCoor,
  placeholder,
  disabled,
  className = '',
  dropdownClassName = '',
  error = false,
  ...rest
}) => {
  const [searchValue, setSearchValue] = useState<string>(value || '');
  const [places, setPlaces] = useState<Place[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const justSelectedRef = useRef<boolean>(false);
  const userTypingRef = useRef<boolean>(false);

  useEffect(() => {
    setSearchValue(value || '');
  }, [value]);

  useEffect(() => {
    const handler = setTimeout(() => {
      // Only search if user is actively typing and didn't just select an option
      if (justSelectedRef.current || !userTypingRef.current) {
        return;
      }

      if (searchValue && searchValue.length > 3) {
        void fetchPlaces(searchValue);
      } else {
        setPlaces([]);
        setIsOpen(false);
      }
    }, 250);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const fetchPlaces = async (query: string) => {
    setIsLoading(true);
    try {
      const data: getPlacesRequest = await graphQlClient.request(
        MapsGraphQL.queries.getAutocomplete,
        { input: query },
      );
      setPlaces(data.autocomplete);
      setIsOpen(data.autocomplete.length > 0);
    } catch (e) {
      setPlaces([]);
      setIsOpen(false);
      // eslint-disable-next-line no-console
      console.error('GoogleAddressInput: failed to fetch places', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (nextValue: string) => {
    justSelectedRef.current = false; // Allow new searches when user types
    userTypingRef.current = true; // User is actively typing
    setSearchValue(nextValue);
    onChange(nextValue);
    if (onChangeCoor) {
      onChangeCoor(null, null);
    }
  };

  const handleInputFocus = () => {
    // Don't automatically search on focus - wait for user to type
    // This prevents the dropdown from opening when focusing on an already selected address
  };

  const handleInputBlur = () => {
    // Reset typing flag when input loses focus
    userTypingRef.current = false;

    // Only close dropdown if we're not selecting something
    setTimeout(() => {
      if (!justSelectedRef.current) {
        setIsOpen(false);
      }
    }, 150);
  };

  const handleSelect = (option: Place) => {
    justSelectedRef.current = true;
    userTypingRef.current = false; // User is no longer typing
    onChange(option.description);
    setSearchValue(option.description);
    setIsOpen(false);
    setPlaces([]);
    if (onChangeCoor) {
      onChangeCoor(option.latitude, option.longitude);
    }

    // Reset the flag after a delay to allow for new searches
    setTimeout(() => {
      justSelectedRef.current = false;
    }, 500);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <Input
        value={searchValue}
        onChange={(e) => handleInputChange(e.currentTarget.value)}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        className={`${isOpen ? 'rounded-b-none' : ''} ${className}`}
        autoComplete="off"
        {...rest}
      />
      {isLoading && (
        <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
          <svg
            className="h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
        </div>
      )}
      {isOpen && places.length > 0 && (
        <ul
          className={`absolute z-20 max-h-60 w-full overflow-auto rounded-b-md border border-t-0 bg-white text-sm shadow-md dark:border-gray-700 dark:bg-gray-800 ${dropdownClassName}`}>
          {places.map((option) => (
            <li
              key={`${option.place_id}-${option.latitude}-${option.longitude}`}
              className="cursor-pointer px-3 py-2 text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
              onClick={() => handleSelect(option)}>
              {option.description}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GoogleAddressInput;
